<template>
  <b-form class="form-login" @submit="onSubmit" @reset="onReset">
    <slot></slot>
    <b-form-group v-if="signup_setting_hint" class="text-description">
      {{ signup_setting_hint }}
    </b-form-group>

    <template v-if="!disableSignIn">
        <b-form-group
            class="group-item"
            :label="displayPlaceholderOfUsername"
            label-for="login-name"
        >
            <b-form-input
            id="login-name"
            v-model="form.username"
            type="text"
            required
            ></b-form-input>
        </b-form-group>

        <b-form-group
            class="group-item"
            :label="displayPlaceholderOfPassword"
            label-for="login-password"
        >
            <b-form-input
            id="login-password"
            v-model="form.password"
            type="password"
            required
            ></b-form-input>
        </b-form-group>

        <div class="signin-actions">
            <b-button
            class="btn-submit new-design-button signin-btn"
            type="submit"
            variant="primary"
            >
            Login
            </b-button>

            <router-link
            v-if="!isTypeNoOne"
            class="btn-link-lg sign-up-link"
            :to="'register'"
            >
            Sign Up
            </router-link>
        </div>
    </template>

    <div class="d-flex flex-column row-gap-2"
        :class="['ms-signin-section', { 'with-divider pt-4': !disableSignIn }]"
    >
      <a
        v-if="enableMSSignIn"
        class="btn-submit new-design-button ms-signin-button d-flex align-items-center justify-content-center gap-2 w-100 text-white"
        :href="redirectMSURL"
        role="button"
        aria-label="Sign in with Microsoft Azure AD"
        target="_blank"
      >
        <font-awesome-icon :icon="['fab', 'microsoft']" aria-hidden="true" />
        <span class="ms-signin-text ml-2">
          <strong>Sign In with Microsoft Azure AD</strong>
        </span>
      </a>
      <a
        v-if="showAlreadyLoggedInContinue"
        class="btn-submit new-design-button ms-signin-button already-logged-in-btn d-flex align-items-center justify-content-center gap-2 w-100"
        :href="continueUrl"
        target="_blank"
        >
        <strong>
            Already logged in to CN? Continue
        </strong>
        </a>
    </div>

    <div class="aux-links">
      <div class="search-eportfolios-link">
        <router-link :to="'index'">
          <span>Search ePortfolios at {{ title }}</span>
        </router-link>
      </div>

      <div v-if="!hideForgotPassword" class="forgot-password-section">
        <a
          class="forgot-password"
          target="_blank"
          :href="displayLinkOfForgotPassword"
        >
          Forgot Password
        </a>
      </div>
    </div>

    <b-modal ref="my-modal" title="Message">
      <p v-text="message"></p>
    </b-modal>
  </b-form>
</template>

<script>
import { CN_HOST } from '@/configs';
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'form-login',
  props: {
    placeholderOfUsername: {
      type: String,
      default: 'Email or CN Number'
    },
    placeholderOfPassword: {
      type: String,
      default: 'Password'
    },
    forgotUrl: {
      type: String,
      default: `${CN_HOST}/requestresetpassword`
    }
  },
  data() {
    return {
      form: {
        username: '',
        password: ''
      },
      message: '',
      isAlreadyLoggedIn: false
    };
  },
  computed: {
    redirectMSURL() {
      var brand = encodeURIComponent(
        this.brand
      );
      var signinRedirectionPage = encodeURIComponent(
        this.signin_redirection_page
      );
      return `${CN_HOST}/program/oauth2azure/login?brand=${brand}&redirect=${signinRedirectionPage}`;
    },
    disableSignIn() {
      return this.signin_method_cn_login_disabled;
    },
    enableMSSignIn() {
      return this.signin_method_ms_azure_enabled;
    },
    hideForgotPassword() {
      return this.signin_link_of_forgot_password_disabled;
    },
    displayPlaceholderOfUsername() {
      return this.signin_placeholder_of_username || this.placeholderOfUsername;
    },
    displayPlaceholderOfPassword() {
      return this.signin_placeholder_of_password || this.placeholderOfPassword;
    },
    displayLinkOfForgotPassword() {
      return this.signin_link_of_forgot_password || this.forgotUrl;
    },
    showAlreadyLoggedInContinue() {
      return this.isAlreadyLoggedIn;
    },
    continueUrl() {
      if (!this.signin_redirection_page) {
        return CN_HOST;
      }
      if (/^(https?:)?\/\//i.test(this.signin_redirection_page)) {
        return this.signin_redirection_page;
      }
      const redirectionPath = this.signin_redirection_page.replace(/^\/+/, '');
      return `${CN_HOST}/${redirectionPath}`;
    },
    ...mapState('siteBrand', [
      'title',
      'brand',
      'heading',
      'signup_setting_hint',
      'signin_redirection_page',
      'signin_placeholder_of_username',
      'signin_placeholder_of_password',
      'signin_link_of_forgot_password',
      'signin_link_of_forgot_password_disabled',
      'signin_method_cn_authed_enabled',
      'signin_method_cn_login_disabled',
      'signin_method_ms_azure_enabled'
    ]),
    ...mapGetters('siteBrand', ['isTypeNoOne'])
  },
  mounted() {
    if (this.signin_method_cn_authed_enabled) {
      this.fetchAuthState();
    }
  },
  methods: {
    async fetchAuthState() {
      try {
        const { data } = await this.$http.get('auth_state', {
          withCredentials: true
        });
        const hasSessionData =
          Array.isArray(data?.data) && data.data.length > 0;
        this.isAlreadyLoggedIn = Boolean(hasSessionData && data?.result);
      } catch (error) {
        this.isAlreadyLoggedIn = false;
      }
    },
    onSubmit: async function(evt) {
      evt.preventDefault();
      this.message = '';
      try {
        const { data } = await this.$http.get('auth', {
          headers: {
            username: this.form.username,
            password: this.form.password
          },
          params: {
            action: 'login'
          }
        });
        const {
          data: { token }
        } = data;
        if (token) {
          if (this.signin_redirection_page) {
            var signinRedirectionPage = encodeURIComponent(
              this.signin_redirection_page
            );
            window.location.href = `${CN_HOST}/site/token/${token}/redirect_to/${signinRedirectionPage}`;
          } else {
            window.location.href = `${CN_HOST}/site/token/${token}`;
          }
        } else if (data.errors && data.errors.length) {
          this.message = data.errors.join(',');
          this.$refs['my-modal'].show();
        }
      } catch (error) {
        this.message = 'Login failed. Please try again.';
        this.$refs['my-modal'].show();
      }
    },
    onReset(evt) {
      evt.preventDefault();
    }
  }
};
</script>

<style scoped lang="scss">
.form-login {
  display: flex;
  flex-direction: column;
  gap: rem(18);
  max-width: rem(620);
  margin: 0 auto;

  .group-item {
    margin: 0;
    color: #1a374d;
    opacity: 0.9;

    ::v-deep label {
      margin-bottom: rem(10);
      font-weight: 600;
    }

    ::v-deep .form-control {
      height: rem(48);
      background: #ffffff;
      border: rem(1) solid #babdcc;
      border-radius: rem(8);
    }
  }
}

.new-design-button {
  width: rem(160);
  height: rem(44);
  background: #3574d9;
  box-shadow: 0 0 rem(14) rgba(0, 0, 0, 0.1);
  border-radius: rem(8);
}

.signin-actions {
  display: flex;
  align-items: center;
  gap: rem(16);
  flex-wrap: wrap;
}

.sign-up-link {
  margin-left: 0;
  white-space: nowrap;
  font-weight: 600;
}

.ms-signin-section.with-divider {
  position: relative;
  border-top: rem(1) solid #e6ebf2;
}

.ms-signin-section.with-divider::before {
  content: "Or";
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 0 rem(10);
  background: #ffffff;
  color: #8d97ab;
  font-size: rem(14);
  font-weight: 600;
}

.ms-signin-button {
  width: 100%;
  height: rem(48);
}

.ms-signin-text {
  padding: rem(8) 0;
}

.already-logged-in-btn {
  background: #ffffff;
  border: rem(1) solid #3574d9;
  color: #3574d9;
}

.aux-links {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: rem(12);
  flex-wrap: wrap;
}

.forgot-password-section {
  display: flex;
  justify-content: flex-end;
}

.forgot-password {
  font-weight: 600;
  font-size: rem(14);
  line-height: rem(20);
  color: #3574d9;
}

.search-eportfolios-link {
  margin: 0;
  font-weight: 600;
}

@media all and (max-width: 768px) {
  .form-login {
    gap: rem(14);
  }

  .signin-actions {
    align-items: stretch;
    gap: rem(12);
  }

  .signin-btn {
    width: 100%;
  }

  .aux-links {
    flex-direction: column-reverse;
    align-items: flex-start;
  }
}
</style>
